<script setup lang="ts">
import { inject, onMounted, ref } from "vue";
import { CartService } from "../services/cart.service";
import { SITEURL } from "@libTs/cms.globals";
import { itemCodeToUrlSlug } from "../helpers/url.helper";
import { vClickOutside } from "../directives/v-click-outside";

const cartSvc = inject<CartService>("cartSvc") as CartService;

const numberOfItems = ref(0);

onMounted(() => {
    document.addEventListener("cartLoaded", getItemNumber);

    numberOfItems.value = cartSvc.cartObj.carts[0].items.length;
});

const getItemNumber = () => {
    numberOfItems.value = cartSvc.cartObj.carts[0].items.length;
};

const formatAsCurrency = (value: number) => {
    return new Intl.NumberFormat("de-CH", {
        style: "currency",
        currency: "CHF",
    }).format(value);
};
</script>

<template>
    <div
        class="relative border-b-4 h-full flex items-center w-6 justify-center border-t-transparent border-t-4 cart-list"
        :class="{
            'border-b-primary': cartSvc.openCart,
            'border-b-transparent': !cartSvc.openCart,
        }"
        v-click-outside="() => (cartSvc.openCart = false)"
    >
        <button
            class="btn-cart relative"
            @click="cartSvc.openCart = !cartSvc.openCart"
            title="Cart"
        >
            <div
                class="h-4 w-4 bg-primary text-white rounded-full flex items-center justify-center text-f7 font-bold absolute -top-2 -right-2 leading-4"
                v-if="
                    cartSvc.cartObj.carts[0].items.length > 0 ||
                    numberOfItems > 0
                "
            >
                {{ cartSvc.cartObj.carts[0].items.length ?? numberOfItems }}
            </div>
        </button>
        <div
            v-if="cartSvc.openCart"
            class="absolute top-20 -right-20 w-auto min-w-[650px] bg-white shadow-lg z-50 pt-8 pb-8 px-12"
        >
            <button
                @click="cartSvc.openCart = false"
                class="absolute top-6 right-6 text-grey-500 hover:text-grey-700"
            >
                <i class="fal fa-times text-f3"></i>
            </button>
            <div class="pb-4 flex justify-between items-center">
                <h3 class="text-f4 font-bold">Your cart</h3>
            </div>
            <div
                v-if="
                    !cartSvc.cartObj.carts[0] ||
                    (cartSvc.cartObj.carts[0].items.length === 0 &&
                        cartSvc.cartObj.carts.length <= 1)
                "
                class="py-4"
            >
                Your cart is empty.
            </div>
            <div
                v-else-if="
                    !cartSvc.cartObj.carts[0] ||
                    (cartSvc.cartObj.carts[0].items.length === 0 &&
                        cartSvc.cartObj.carts.length > 1)
                "
                class="py-4"
            >
                <div class="mb-4">
                    You have prepared carts. Go to the cart page to view them.
                </div>
                <div class="">
                    <a href="/shop/cart" class="btn-cb w-full">
                        <span class="font-bold">Go to cart</span>
                        <i class="fas fa-shopping-cart"></i>
                    </a>
                </div>
            </div>
            <ul v-else class="scroll-container max-h-[300px]">
                <li
                    v-for="(item, index) in cartSvc.cartObj.carts[0].items"
                    :key="index"
                    class="py-4 border-b border-grey-200 flex items-start"
                >
                    <div class="flex items-start w-full gap-5">
                        <img
                            v-if="item.overlayData && item.overlayData.ImageURL"
                            :src="item.overlayData.ImageURL"
                            alt="Item Image"
                            class="w-44 h-24 object-contain mr-4 rounded-sm shrink-0 grow-0"
                        />
                        <div
                            class="bg-grey-30 rounded-sm w-44 h-24 mr-4 flex items-center justify-center shrink-0 grow-0"
                            v-else
                        >
                            <img
                                :src="SITEURL + 'assets/icons/no-photo.svg'"
                                alt="No Image Available"
                                class="w-4 h-4 object-contain"
                            />
                        </div>
                        <div class="flex flex-col w-full gap-4 h-full">
                            <div
                                class="flex flex-row justify-between items-start gap-4"
                            >
                                <a
                                    :href="'/product/' + itemCodeToUrlSlug(item.ItemCode)"
                                    class="flex-grow"
                                >
                                    <h4
                                        class="text-f6 font-bold break-word hyphens-auto"
                                    >
                                        {{ item.ItemName }}
                                    </h4>
                                </a>
                                <div class="flex flex-row items-center gap-2">
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-10 rounded-s font-bold text-f6"
                                        @click="
                                            item.quantity > 1
                                                ? item.quantity--
                                                : null;
                                            cartSvc.updateTotalPrice();
                                        "
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        v-model="item.quantity"
                                        @input="cartSvc.updateTotalPrice()"
                                        class="w-12 text-center border-none text-f6 font-bold quantity-input"
                                        min="1"
                                    />
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-100 rounded-s font-bold text-f6"
                                        @click="
                                            item.quantity++;
                                            cartSvc.updateTotalPrice();
                                        "
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    class=""
                                    @click.stop="
                                        cartSvc.removeFromCart(
                                            item.ItemCode,
                                            item.quantity,
                                            cartSvc.cartObj.carts[0],
                                        )
                                    "
                                >
                                    <i class="far fa-trash"></i>
                                </button>
                            </div>
                            <div class="flex justify-between items-center">
                                <div class="text-f7">
                                    Art. Nr. {{ item.ItemCode }}
                                </div>
                                <div class="text-f5 font-bold">
                                    {{ formatAsCurrency(item.Preis) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
            <div class="" v-if="cartSvc.cartObj.carts[0].items.length > 0">
                <div class="py-8 border-b border-grey-200">
                    Shipping (freight costs, VOC) will be calculated at the end.
                </div>
                <div class="py-8 flex justify-between items-center">
                    <div class="text-f4 font-bold">Total</div>
                    <div class="text-f4 font-bold">
                        {{
                            formatAsCurrency(
                                cartSvc.cartObj.carts[0].totalPrice,
                            )
                        }}
                    </div>
                </div>
                <div class="">
                    <a href="/shop/cart" class="btn-cb w-full">
                        <span class="font-bold">Go to cart</span>
                        <i class="fas fa-shopping-cart"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
